import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UsersThree, Plus, Trash, ShieldCheck, Warning } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin (full access)' },
  { value: 'manager', label: 'Manager (all pages, no user mgmt)' },
  { value: 'stocker', label: 'Stocker (Put Away + Picking)' },
  { value: 'picker', label: 'Picker (Put Away + Picking)' },
];

export default function UsersPage({ user: currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'stocker' });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('warehouse_token');
      const res = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setCreating(true);
    try {
      const token = localStorage.getItem('warehouse_token');
      await axios.post(`${API}/auth/register`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Account created — share the password with ${form.name}`);
      setShowAdd(false);
      setForm({ email: '', name: '', password: '', role: 'stocker' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (u) => {
    if (u.id === currentUser.id) {
      toast.error('You cannot delete your own account');
      return;
    }
    if (!window.confirm(`Remove ${u.name} (${u.email})? They will lose access immediately.`)) return;
    try {
      const token = localStorage.getItem('warehouse_token');
      await axios.delete(`${API}/users/${u.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User removed');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove user');
    }
  };

  const handleFactoryReset = async () => {
    const confirmMsg = 'FACTORY RESET will delete ALL locations, inventory, tasks, items, replenishment rules and adjustment history.\n\nUser accounts are kept.\n\nType DELETE to confirm.';
    const input = window.prompt(confirmMsg);
    if (input !== 'DELETE') {
      if (input !== null) toast.error('Reset cancelled — you must type DELETE exactly');
      return;
    }
    try {
      const token = localStorage.getItem('warehouse_token');
      const res = await axios.post(`${API}/admin/factory-reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const deleted = res.data.deleted || {};
      const summary = Object.entries(deleted).map(([k, v]) => `${k}: ${v}`).join(' · ');
      toast.success(`Factory reset done — ${summary}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Factory reset failed');
    }
  };


  return (
    <div className="p-8" data-testid="users-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="heading text-3xl font-black text-[#09090B] mb-2" data-testid="users-title">
            User Accounts
          </h1>
          <p className="text-[#52525B]">Create accounts and hand over the login details to your team.</p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="btn-primary flex items-center gap-2"
          data-testid="add-user-button"
        >
          <Plus size={18} weight="bold" />
          Add User
        </button>
      </div>

      {currentUser.role === 'admin' && (
        <div className="card mb-6 border-2 border-[#DC2626]/30 bg-[#FEF2F2]" data-testid="factory-reset-card">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-[#DC2626] flex items-center justify-center flex-shrink-0">
              <Warning size={20} weight="fill" className="text-white" />
            </div>
            <div className="flex-1">
              <h3 className="heading text-base font-black text-[#09090B] mb-1">Factory Reset</h3>
              <p className="text-sm text-[#52525B] mb-3">
                Wipes ALL locations, inventory, tasks, items, replenishment rules and adjustment history.
                Your user accounts are preserved so you can log back in. This cannot be undone.
              </p>
              <button
                onClick={handleFactoryReset}
                className="text-xs font-bold px-4 py-2 rounded-md bg-[#DC2626] text-white hover:bg-[#B91C1C]"
                data-testid="factory-reset-button"
              >
                Factory Reset Warehouse Data
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="card text-center py-12">Loading...</div>
      ) : users.length === 0 ? (
        <div className="card text-center py-12" data-testid="users-empty">
          <UsersThree size={40} className="mx-auto text-[#52525B] mb-3" weight="duotone" />
          <p className="text-[#52525B]">No user accounts yet.</p>
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="border-b border-[#E4E4E7] text-left">
                <th className="p-3 text-xs font-medium text-[#52525B] uppercase">Name</th>
                <th className="p-3 text-xs font-medium text-[#52525B] uppercase">Email</th>
                <th className="p-3 text-xs font-medium text-[#52525B] uppercase">Role</th>
                <th className="p-3 text-xs font-medium text-[#52525B] uppercase">Created</th>
                <th className="p-3 text-xs font-medium text-[#52525B] uppercase text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-[#E4E4E7] hover:bg-[#FAFAFA]" data-testid="users-row">
                  <td className="p-3 font-bold text-[#09090B]">{u.name}</td>
                  <td className="p-3 mono text-[#09090B]">{u.email}</td>
                  <td className="p-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-bold uppercase ${
                      u.role === 'admin' ? 'bg-[#DC2626] text-white' :
                      u.role === 'manager' ? 'bg-[#007AFF] text-white' :
                      'bg-[#F4F4F5] text-[#09090B]'
                    }`}>
                      {u.role === 'admin' && <ShieldCheck size={12} weight="fill" />}
                      {u.role}
                    </span>
                  </td>
                  <td className="p-3 mono text-xs text-[#52525B]">
                    {u.created_at ? String(u.created_at).slice(0, 10) : '-'}
                  </td>
                  <td className="p-3 text-right">
                    {u.id !== currentUser.id ? (
                      <button
                        onClick={() => handleDelete(u)}
                        className="btn-secondary text-xs px-3 py-1 inline-flex items-center gap-1"
                        data-testid="users-delete-button"
                      >
                        <Trash size={14} weight="bold" />
                        Remove
                      </button>
                    ) : (
                      <span className="text-xs text-[#A1A1AA]">You</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle className="heading text-xl font-bold">Create User Account</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label htmlFor="u_name">Full Name *</Label>
              <Input id="u_name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="add-user-name-input" />
            </div>
            <div>
              <Label htmlFor="u_email">Email *</Label>
              <Input id="u_email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="add-user-email-input" />
            </div>
            <div>
              <Label htmlFor="u_password">Password *</Label>
              <Input id="u_password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min 6 characters" required data-testid="add-user-password-input" />
              <p className="text-xs text-[#52525B] mt-1">You will hand this password to the user. They can change it later once we add a profile page.</p>
            </div>
            <div>
              <Label htmlFor="u_role">Role *</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="add-user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={creating} className="flex-1 btn-primary" data-testid="add-user-submit-button">
                {creating ? 'Creating...' : 'Create Account'}
              </button>
              <button type="button" onClick={() => setShowAdd(false)} className="flex-1 btn-secondary" data-testid="add-user-cancel-button">
                Cancel
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
